/**
 * Narxnoma (price-list) PDF — umumiy generator.
 * Narxnoma sahifasi, Kirim (prixod) ro'yxati va Mahsulotlar bo'limi shu modulni ishlatadi.
 *  - buildNarxnomaBody(items, mode): katalog HTML (preview va print uchun)
 *  - printNarxnoma(items, mode): alohida A4 oynada chop etadi
 * items[] elementi: { extra_name, artikul, brand_name, brand_image, model_id, model_name,
 *                     model_image, unit_name, optom_price, optom_dollar_price,
 *                     chakana_price, chakana_dollar_price }
 * mode: 'optom' | 'chakana'
 */

export const NX_CSS = `
/* Chop etishda fon ranglari (qizil panel, qora sarlavha) chiqishi uchun — majburlaymiz */
.nx-sheet, .nx-sheet * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
.nx-sheet { width: 794px; max-width: 100%; margin: 0 auto; background: #fff; padding: 14px 18px 24px; color: #111; font-family: Arial, Helvetica, sans-serif; box-sizing: border-box; }
.nx-head { display: flex; justify-content: space-between; align-items: flex-start; padding-bottom: 10px; border-bottom: 4px solid #111; }
.nx-brand-main { font-size: 34px; font-weight: 900; font-style: italic; letter-spacing: -1px; color: #111; }
.nx-red { color: #e11d2a; }
.nx-brand-sub { font-size: 12px; font-weight: 800; letter-spacing: 3px; color: #111; margin-top: -2px; }
.nx-warranty { border: 2px solid #f59e0b; border-radius: 8px; padding: 6px 14px; text-align: center; }
.nx-w1 { color: #b45309; font-weight: 900; font-style: italic; font-size: 15px; }
.nx-w2 { color: #b45309; font-weight: 700; font-size: 9px; letter-spacing: 1px; }
.nx-pricetype { text-align: center; font-weight: 900; letter-spacing: 2px; color: #e11d2a; font-size: 13px; margin: 8px 0 4px; }
.nx-thead { display: flex; align-items: center; background: #1f2937; color: #fff; font-weight: 800; font-size: 12px; letter-spacing: 1px; padding: 9px 12px; }
.nx-c-name { flex: 1; }
.nx-c-art { width: 150px; }
.nx-c-price { width: 120px; text-align: right; }
.nx-group { margin-top: 10px; }
.nx-g-bar { display: flex; align-items: center; gap: 10px; background: #e11d2a; color: #fff; padding: 7px 12px; border-radius: 3px; }
.nx-g-img { width: 26px; height: 26px; object-fit: contain; background: #fff; border-radius: 4px; padding: 1px; }
.nx-g-emoji { font-size: 18px; }
.nx-g-title { font-weight: 900; font-style: italic; font-size: 15px; letter-spacing: 0.5px; }
.nx-row { display: flex; align-items: center; padding: 7px 12px; border-bottom: 1px solid #eee; background: #fafafa; }
.nx-row:nth-child(even) { background: #f1f5f9; }
.nx-name-cell { flex: 1; display: flex; align-items: center; gap: 10px; min-width: 0; }
.nx-badge { color: #fff; font-size: 9px; font-weight: 800; padding: 4px 9px; border-radius: 4px; letter-spacing: 0.5px; white-space: nowrap; }
.nx-komplekt { background: #2563eb; }
.nx-dona { background: #374151; }
.nx-name { font-weight: 800; font-style: italic; color: #111; font-size: 14px; word-break: break-word; }
.nx-art { width: 150px; }
.nx-art-pill { background: #e5e7eb; color: #374151; font-style: italic; font-weight: 700; font-size: 12px; padding: 3px 10px; border-radius: 6px; }
.nx-price { width: 120px; text-align: right; color: #16a34a; font-weight: 900; font-style: italic; font-size: 15px; }
.nx-empty { text-align: center; color: #94a3b8; font-style: italic; padding: 30px; }
`;

const escapeHtml = s => String(s == null ? '' : s).replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));

function imgUrl(name) {
  if (!name) return '';
  const base = (window.axios && window.axios.defaults && window.axios.defaults.baseURL) || '';
  return base + (base.endsWith('/') ? '' : '/') + 'api/v1/uploads/' + name;
}

function badge(it) {
  const u = (it.unit_name || '').toLowerCase();
  const isKomplekt = u.includes('komplekt') || u.includes('комплект') || u.includes('компл');
  return { label: (it.unit_name || 'DONA').toUpperCase(), cls: isKomplekt ? 'komplekt' : 'dona' };
}

function priceText(it, mode, currency) {
  if (currency === 'som') {
    const som = mode === 'optom' ? (Number(it.optom_price) || 0) : (Number(it.chakana_price) || 0);
    return som > 0 ? som.toLocaleString('ru-RU') + " so'm" : '—';
  }
  // dollar (default)
  const dol = mode === 'optom' ? (Number(it.optom_dollar_price) || 0) : (Number(it.chakana_dollar_price) || 0);
  return dol > 0 ? '$ ' + dol.toFixed(2).replace('.', ',') : '—';
}

// Mahsulotlarni model bo'yicha guruhlash (model rasmi bilan)
function groupByModel(items) {
  const map = new Map();
  for (const it of (items || [])) {
    const key = it.model_id != null ? 'm' + it.model_id : (it.brand_id != null ? 'b' + it.brand_id : 'x');
    if (!map.has(key)) {
      map.set(key, {
        title: (it.model_name || it.brand_name || 'BOSHQALAR').toUpperCase(),
        image: imgUrl(it.model_image || it.brand_image),
        items: [],
      });
    }
    map.get(key).items.push(it);
  }
  return Array.from(map.values()).sort((a, b) => a.title.localeCompare(b.title));
}

export function buildNarxnomaBody(items, mode, currency) {
  currency = currency === 'som' ? 'som' : 'dollar';
  const groups = groupByModel(items);
  const curLabel = currency === 'som' ? "SO'M" : '$';
  let html = '';
  html += `<div class="nx-head">
    <div class="nx-brand">
      <div class="nx-brand-main">PROFCAR<span class="nx-red">_UZ</span></div>
      <div class="nx-brand-sub">PREMIUM AMORTIZATORLAR</div>
    </div>
    <div class="nx-warranty"><div class="nx-w1">3 OY KAFOLAT</div><div class="nx-w2">YOKI 15 000 KM</div></div>
  </div>`;
  html += `<div class="nx-pricetype">${mode === 'optom' ? 'OPTOM NARXLAR' : 'CHAKANA NARXLAR'} (${curLabel})</div>`;
  html += `<div class="nx-thead"><span class="nx-c-name">MAHSULOT NOMI</span><span class="nx-c-art">ARTIKUL</span><span class="nx-c-price">NARX</span></div>`;

  if (!groups.length) {
    html += `<div class="nx-empty">Mahsulot topilmadi</div>`;
    return html;
  }

  for (const g of groups) {
    const icon = g.image
      ? `<img class="nx-g-img" src="${g.image}" onerror="this.style.display='none'"/>`
      : `<span class="nx-g-emoji">🚗</span>`;
    html += `<div class="nx-group"><div class="nx-g-bar">${icon}<span class="nx-g-title">${escapeHtml(g.title)}</span></div>`;
    for (const it of g.items) {
      const b = badge(it);
      const art = it.artikul ? `<span class="nx-art-pill">${escapeHtml(it.artikul)}</span>` : '';
      html += `<div class="nx-row">
        <span class="nx-name-cell"><span class="nx-badge nx-${b.cls}">${escapeHtml(b.label)}</span><span class="nx-name">${escapeHtml(it.extra_name)}</span></span>
        <span class="nx-art">${art}</span>
        <span class="nx-price">${priceText(it, mode, currency)}</span>
      </div>`;
    }
    html += `</div>`;
  }
  return html;
}

export function printNarxnoma(items, mode, currency) {
  const body = buildNarxnomaBody(items, mode, currency);
  const w = window.open('', '_blank', 'width=900,height=1200');
  if (!w) return false;
  w.document.write(`<!DOCTYPE html>
<html><head><meta charset="utf-8"><title>Narxnoma — ${mode === 'optom' ? 'Optom' : 'Chakana'}</title>
<style>${NX_CSS}
@page { size: A4; margin: 8mm; }
html, body { margin: 0; background: #fff; }
* { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; color-adjust: exact !important; }
</style></head>
<body><div class="nx-sheet">${body}</div></body></html>`);
  w.document.close();
  w.focus();
  setTimeout(() => { w.print(); }, 700);
  return true;
}
